// Estado inicial del flow de aporte de producto. Dos CTAs apiladas
// verticalmente (uno arriba, otro abajo) — no en fila, porque en el
// panel lateral desktop el ancho es limitado y compartir el espacio
// entre dos botones full-width los achica demasiado.
//
// Los productos usados en la última generación ya no tienen un CTA aparte:
// aparecen primeros y con badge "Último utilizado" dentro de "Elegir de
// mis productos" (ver PickProductPanel).

import React from 'react';
import { ChevronRight, PackageX } from 'lucide-react';
import { AbanicoDeFotos } from '../../../components/AbanicoDeFotos';
import { useUserProducts, useUserProductsLoaded } from '../productsContext';
import type { PresetV2 } from '../mockData';
import CachedImg from '../../../components/CachedImg';
import { palabraDeModeloActual } from '../../../lib/nombreDeModelo';

export interface ChooseProductPanelProps {
  onShowProducts: () => void;
  /** "Subir nuevo producto" — NO abre el file picker directo: pasa primero
   *  por la pantalla explicativa (estado vacío de UploadProductPanel) donde
   *  se ve cómo subir el producto y recién ahí se elige la foto. */
  onStartNewProduct: () => void;
  /** Kind del preset abierto — controla la copy gris (el texto cambia
   *  para que sea consistente con el modo: template/design hablan de
   *  "preset", design-modelo de "elegir modelo / pose / fondo",
   *  design-auto de "armar la escena automáticamente"). */
  presetKind?: PresetV2['kind'];
  /** "Generar sin producto" (ADR-673). Solo se renderiza si además el preset
   *  es `video-influencer`: es el único modo que puede generar algo sin una
   *  foto de producto (la influencer habla a cámara). */
  onSkipProduct?: () => void;
}

export const ChooseProductPanel: React.FC<ChooseProductPanelProps> = ({
  onShowProducts,
  onStartNewProduct,
  presetKind,
  onSkipProduct,
}) => {
  // Productos del user — si no tiene ninguno, ocultamos el CTA "Elegir de
  // mis productos" (no tendría qué mostrar). Los 3 más recientes (la query
  // viene ordenada por created_at desc) se usan como thumbnails apilados en
  // ese botón, en lugar del ícono de caja.
  const products = useUserProducts();
  const productsLoaded = useUserProductsLoaded();
  const recentThumbs = products.slice(0, 3);
  // Mostramos el CTA "Elegir de mis productos" de forma OPTIMISTA: mientras
  // el catálogo todavía carga (`!productsLoaded`) lo dejamos visible para que
  // el botón no aparezca "tarde" al abrir el modo. Solo lo ocultamos si tras
  // cargar confirmamos que el user no tiene productos. Los thumbnails entran
  // cuando resuelve el fetch.
  const showPickButton = !productsLoaded || products.length > 0;

  return (
    <div>
      <div className="mb-5">
        <div>
          <p className="text-[17px] font-semibold leading-tight text-[#16181D]">
            Elegí el producto para generar
          </p>
          <p className="mt-1 text-[13.5px] leading-snug text-black/45">
            {presetKind === 'design-auto'
              ? 'Atlas va a usar esta foto y armar la escena entera con IA — sin configurar nada más.'
              : presetKind === 'design-modelo'
                ? `Atlas va a usar esta foto y combinarla con el ${palabraDeModeloActual().singular}, la pose y el fondo que elijas.`
                : presetKind === 'video-influencer'
                  ? 'Atlas va a usar esta foto para generar un video de un influencer IA presentando tu producto.'
                  : presetKind === 'fotos-producto'
                    ? 'Atlas va a generar las vistas que elijas de cada producto — podés seleccionar varios.'
                    : 'Atlas va a usar esta foto para aplicar el preset elegido y armar la imagen final.'}
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-3">
        {/* La tarjeta de subir de Mi marca, con el abanico —
            ADR-2026-10-06-subir-producto-en-los-modos-con-la-cara-de-mi-marca. */}
        <button
          type="button"
          onClick={onStartNewProduct}
          className="group relative flex w-full flex-col items-center justify-center gap-4 overflow-hidden rounded-3xl border-[1.5px] border-dashed border-[#C9D3FF] bg-gradient-to-b from-white to-[#F1F4FF] px-5 py-7 text-center transition-[border-color,box-shadow] duration-300 hover:border-brand-primary hover:shadow-[0_12px_32px_-14px_rgba(22,68,235,0.45)] active:scale-[0.99]"
        >
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-2/3 bg-[radial-gradient(60%_70%_at_50%_0%,rgba(61,92,255,0.10),transparent)] opacity-70 transition-opacity duration-300 group-hover:opacity-100"
          />
          <AbanicoDeFotos />
          <span className="relative">
            <span className="block text-[15px] font-semibold leading-tight text-[#16181D]">
              Subir nuevo producto
            </span>
            <span className="mt-1 block text-[13px] leading-snug text-black/45">
              Cargá sus fotos y empezá a crear
            </span>
          </span>
        </button>
        {showPickButton && (
          <button
            type="button"
            onClick={onShowProducts}
            className="group flex w-full items-center gap-3 rounded-2xl border border-black/[0.08] bg-white px-3.5 py-3 text-left text-[15px] font-semibold text-[#16181D] shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-colors hover:border-black/[0.16]"
          >
            {/* Stack horizontal de los 3 productos más recientes — uno al
                lado del otro, solapados. El más reciente queda al frente
                (izquierda). Reemplaza el ícono de caja. */}
            <span className="flex shrink-0 -space-x-2.5">
              {recentThumbs.map((p, i) => (
                <span
                  key={p.id}
                  className="relative w-8 h-8 rounded-lg overflow-hidden bg-black/[0.04] ring-2 ring-white"
                  style={{ zIndex: recentThumbs.length - i }}
                >
                  <CachedImg
                    src={p.imageUrl}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </span>
              ))}
            </span>
            <span className="min-w-0 flex-1">Elegir de mis productos</span>
            <ChevronRight className="h-4 w-4 shrink-0 text-black/30 transition-transform group-hover:translate-x-0.5" />
          </button>
        )}
        {/* "Sin producto" (ADR-673) — SOLO el video con influencer: la
            influencer habla a cámara sin mostrar nada. Va último y con menos
            peso visual que los otros dos: es la salida, no el camino
            principal. */}
        {onSkipProduct && presetKind === 'video-influencer' && (
          <button
            type="button"
            onClick={onSkipProduct}
            className="w-full inline-flex items-center justify-center gap-2.5 py-3 px-6 rounded-full text-[14.5px] font-medium text-black/55 hover:text-[#16181D] hover:bg-black/[0.04] transition-colors"
          >
            <PackageX className="w-[18px] h-[18px]" />
            Generar sin producto
          </button>
        )}
      </div>
    </div>
  );
};
